import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { motion } from 'motion/react';
import { ArrowLeft, BookOpen, Printer, Sparkles } from 'lucide-react';
import { ArticleMarkdown } from '@/components/ArticleMarkdown';
import { getResearchArticleView, isSubstackStyleArticle } from '@/components/researchArticleViews';
import {
  marketResearchArticlesService,
  type MarketResearchArticle,
  type MarketResearchArticleTag,
} from '@/services/api';

function handleDownloadPdf(title: string) {
  const originalTitle = document.title;
  document.title = title;
  window.dispatchEvent(new Event('resize'));
  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      window.print();
    });
    window.addEventListener(
      'afterprint',
      () => {
        document.title = originalTitle;
      },
      { once: true },
    );
  });
}

function tagClass(tone: MarketResearchArticleTag['tone']) {
  switch (tone) {
    case 'success':
      return 'bg-emerald-50 text-emerald-800';
    case 'warning':
      return 'bg-amber-50 text-amber-800';
    case 'info':
      return 'bg-violet-50 text-violet-800';
    default:
      return 'bg-gray-100 text-gray-600';
  }
}

function sleeveClass(tone: string) {
  switch (tone) {
    case 'emerald':
      return 'rounded-lg bg-emerald-50 px-3 py-1.5 text-sm font-medium text-emerald-900 transition-colors hover:bg-emerald-900 hover:text-white';
    case 'violet':
      return 'rounded-lg bg-violet-50 px-3 py-1.5 text-sm font-medium text-violet-900 transition-colors hover:bg-violet-900 hover:text-white';
    case 'sky':
      return 'rounded-lg bg-sky-50 px-3 py-1.5 text-sm font-medium text-sky-900 transition-colors hover:bg-sky-900 hover:text-white';
    case 'rose':
      return 'rounded-lg bg-rose-50 px-3 py-1.5 text-sm font-medium text-rose-900 transition-colors hover:bg-rose-900 hover:text-white';
    default:
      return 'rounded-lg bg-gray-100 px-3 py-1.5 text-sm font-medium text-gray-900 transition-colors hover:bg-gray-900 hover:text-white';
  }
}

export function MarketResearchArticlePage() {
  const navigate = useNavigate();
  const { date, articleId } = useParams<{ date: string; articleId: string }>();
  const [article, setArticle] = useState<MarketResearchArticle | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);

  useEffect(() => {
    if (!date || !articleId) {
      setLoadError('Missing article route parameters.');
      setArticle(null);
      return;
    }

    let cancelled = false;
    setArticle(null);
    setLoadError(null);

    marketResearchArticlesService.getArticle(date, articleId).then((payload) => {
      if (cancelled) return;
      if (!payload?.article) {
        setLoadError('Could not load this research article.');
        return;
      }
      setArticle(payload.article);
    });

    return () => {
      cancelled = true;
    };
  }, [date, articleId]);

  const bodyMarkdown = useMemo(() => {
    if (!article?.markdown) return '';
    const startHeading = article.body_start_heading || '## Executive take';
    const lines = article.markdown.split('\n');
    const startIndex = lines.findIndex((line) => line.startsWith(startHeading));
    if (startIndex === -1) return article.markdown;
    return lines.slice(startIndex).join('\n');
  }, [article]);

  const pdfTitle = article
    ? `${article.nav_title || 'Research Article'} — ${article.as_of_label}`
    : 'Research Article';

  const CustomArticleView = getResearchArticleView(articleId);
  const isSubstackArticle = isSubstackStyleArticle(articleId);

  if (!date || !articleId) {
    return (
      <div className="min-h-screen bg-gray-50 p-10">
        <p className="text-sm text-rose-700">Invalid article URL.</p>
      </div>
    );
  }

  return (
    <div
      className={`min-h-screen print:bg-white ${
        isSubstackArticle ? 'bg-[#fffcf8]' : CustomArticleView ? 'bg-[#f5f3ef]' : 'bg-gray-50 ai-receipt-stack-page'
      }`}
    >
      <motion.header
        initial={{ opacity: 0, y: -12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className="ai-receipt-stack-page__header border-b border-gray-200 bg-white print:hidden"
      >
        <div className="mx-auto flex max-w-7xl items-center gap-4 px-6 py-4">
          <button
            type="button"
            onClick={() => navigate('/dashboard')}
            className="rounded-lg p-2 transition-colors hover:bg-gray-100"
            aria-label="Back to dashboard"
          >
            <ArrowLeft className="h-5 w-5" />
          </button>
          <div className="flex min-w-0 flex-1 items-center gap-3">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-violet-600">
              <BookOpen className="h-5 w-5 text-white" />
            </div>
            <div className="min-w-0">
              <h1 className="truncate text-gray-900">
                {article?.nav_title || 'Research Article'}
              </h1>
              <p className="truncate text-sm text-gray-500">
                {article?.as_of_label || date}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={() => handleDownloadPdf(pdfTitle)}
            className="flex shrink-0 items-center gap-2 rounded-lg border border-gray-200 bg-white px-4 py-2 text-sm text-gray-700 transition-colors hover:bg-gray-50"
            disabled={!article}
          >
            <Printer className="h-4 w-4" />
            Download PDF
          </button>
        </div>
      </motion.header>

      <main
        className={
          CustomArticleView
            ? 'print:max-w-none print:p-0'
            : 'ai-receipt-stack-page__main mx-auto max-w-7xl px-6 py-10 print:max-w-none print:px-0 print:py-0'
        }
      >
        {loadError ? (
          <p className="px-6 py-10 text-sm text-rose-700">{loadError}</p>
        ) : !article ? (
          <p className="px-6 py-10 text-sm text-gray-500">Loading research article…</p>
        ) : CustomArticleView ? (
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <CustomArticleView />
          </motion.div>
        ) : (
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="ai-receipt-stack-report mx-auto max-w-3xl print:max-w-none"
          >
            <div className="mb-6 flex flex-wrap items-center gap-2">
              <span className="rounded-lg bg-violet-50 px-2.5 py-0.5 text-xs font-medium text-violet-800">
                Research note
              </span>
              <span className="rounded-lg bg-violet-600 px-2.5 py-0.5 text-xs font-semibold text-white">
                {article.released_label}
              </span>
              <span className="text-sm text-gray-500">{article.audience}</span>
            </div>

            <h2 className="mb-4 text-4xl tracking-tight text-gray-900 md:text-5xl">
              {article.title}
            </h2>

            <div className="mb-6 flex flex-wrap gap-2">
              {article.tags?.map((tag) => (
                <span
                  key={tag.label}
                  className={`rounded-lg px-3 py-1 text-sm ${tagClass(tag.tone)}`}
                >
                  {tag.label}
                </span>
              ))}
            </div>

            <div className="mb-8 rounded-2xl border border-emerald-100 bg-emerald-50/60 p-5">
              <div className="mb-2 flex items-center gap-1.5">
                <Sparkles className="h-4 w-4 text-emerald-800" />
                <p className="text-[11px] font-semibold uppercase tracking-wide text-emerald-800">
                  Executive take
                </p>
              </div>
              <p className="text-sm leading-relaxed text-gray-700">{article.summary}</p>
            </div>

            {article.portfolio_snapshot?.sleeves?.length ? (
              <div className="mb-10 space-y-4">
                <div className="rounded-2xl border border-gray-200 bg-white p-5">
                  <p className="mb-3 text-xs font-semibold uppercase tracking-wide text-gray-500">
                    {article.portfolio_snapshot.title ||
                      'Portfolio snapshot (see article for full construction)'}
                  </p>
                  <div className="space-y-4">
                    {article.portfolio_snapshot.sleeves.map((sleeve) => (
                      <div key={sleeve.label}>
                        <p className="mb-2 text-sm font-medium text-gray-900">{sleeve.label}</p>
                        <div className="flex flex-wrap gap-2">
                          {sleeve.tickers.map((ticker) => (
                            <Link
                              key={ticker}
                              to={`/stock/${ticker}`}
                              className={sleeveClass(sleeve.tone)}
                            >
                              {ticker}
                            </Link>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                  {(article.portfolio_snapshot.how_picked ||
                    article.portfolio_snapshot.full_list_note) && (
                    <div className="mt-4 space-y-2 border-t border-gray-100 pt-4 text-sm leading-relaxed text-gray-600">
                      {article.portfolio_snapshot.how_picked ? (
                        <p>
                          <span className="font-medium text-gray-900">How picked:</span>{' '}
                          {article.portfolio_snapshot.how_picked}
                        </p>
                      ) : null}
                      {article.portfolio_snapshot.full_list_note ? (
                        <p>
                          <span className="font-medium text-gray-900">Full list:</span>{' '}
                          {article.portfolio_snapshot.full_list_note}
                        </p>
                      ) : null}
                    </div>
                  )}
                </div>
              </div>
            ) : null}

            <div className="rounded-3xl border border-gray-200 bg-white p-6 md:p-10 print:rounded-none print:border-0 print:p-0">
              <ArticleMarkdown markdown={bodyMarkdown} />
            </div>
          </motion.div>
        )}
      </main>
    </div>
  );
}
